export const TRIAL_DEFAULT_DAYS = 14;
export const TRIAL_MAX_TEMPLATES = 3;
export const TRIAL_MAX_MANAGERS = 3;

const TRIAL_FEATURES_TEXT =
  "3 templates, up to 3 managers, and real rounds you can run today. No setup fees, no IT project, no commitment.";

export function computeDaysRemainingFromEndDate(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function getTrialVersionLimitations(
  daysRemaining,
  isFree = false,
  limits = null
) {
  const prefix = formatTrialDaysPrefix(daysRemaining, isFree);
  if (limits && (limits.maxTemplates || limits.maxEmployees || limits.maxRooms)) {
    const templates = limits.maxTemplates ?? TRIAL_MAX_TEMPLATES;
    const employees = limits.maxEmployees ?? TRIAL_MAX_MANAGERS;
    const rooms = limits.maxRooms ? `, and ${limits.maxRooms} rooms` : "";
    return `${prefix}: ${templates} templates, up to ${employees} employees${rooms} you can run today. No setup fees, no IT project, no commitment.`;
  }
  return `${prefix}: ${TRIAL_FEATURES_TEXT}`;
}

function formatTrialDaysPrefix(daysRemaining, isFree = false) {
  if (daysRemaining == null || Number.isNaN(Number(daysRemaining))) {
    return isFree
      ? `Your ${TRIAL_DEFAULT_DAYS}-day free version`
      : `Your ${TRIAL_DEFAULT_DAYS}-day trial`;
  }

  const days = Math.max(0, Math.ceil(Number(daysRemaining)));

  if (days <= 0) {
    return isFree ? "Your free version ends today" : "Your trial ends today";
  }
  if (days === 1) {
    return isFree
      ? "Your 1-day free version (1 day left)"
      : "Your 1-day trial (1 day left)";
  }
  return isFree
    ? `Your ${days}-day free version (${days} days left)`
    : `Your ${days}-day trial (${days} days left)`;
}

export function isTrialUser(adminData) {
  if (!adminData) return false;
  const d = (adminData && adminData.data) || adminData;
  if (!d) return false;
  if (d.isPaid === true) return false;
  if (d.isTrial === true || d.isFree === true) return true;
  if (
    isTrialPlanName(d.planName) ||
    isTrialPlanName(d.plan) ||
    isTrialPlanName(d.subscriptionPlanName) ||
    isTrialPlanName(d.pricingPlanName) ||
    isTrialPlanName(d.organizationPlanName)
  ) {
    return true;
  }
  const activeSub =
    d.activeSubscription || d.subscription || d.currentSubscription;
  if (activeSub && isTrialPlanName(activeSub.planName || activeSub.plan)) {
    return true;
  }
  return false;
}

export function isTrialPlanName(planName) {
  const p = String(planName || "")
    .trim()
    .toLowerCase();
  return p === "trial" || p === "free";
}

export function isManagerRoleId(roleId) {
  return Number(roleId) === 5;
}

export function isManagerRoleName(roleName) {
  return String(roleName || "").trim().toLowerCase() === "manager";
}

export function isManagerRole(roleId, roles = []) {
  if (roleId == null || roleId === "") return false;
  if (isManagerRoleId(roleId)) return true;
  if (!Array.isArray(roles) || roles.length === 0) return false;
  const match = roles.find((role) => String(role.roleId) === String(roleId));
  return isManagerRoleName(match?.roleName);
}

export function isTrialEmployeeLimitApiMessage(message) {
  return /\b(only|up to|allowed|maximum|max)\b.*\b(employee|manager)s?\b/i.test(
    String(message || "")
  );
}

export function trialAdminUsersLockedMessage() {
  return "Admin Users is not available during the free trial. Upgrade to manage admin accounts.";
}

export function trialTemplateEditLockedMessage() {
  return "Editing templates is not available during the free trial.";
}

export function trialTemplateDeleteLockedMessage() {
  return "Deleting templates is not available during the free trial.";
}

export function trialTemplateLimitMessage() {
  return `Free trial includes up to ${TRIAL_MAX_TEMPLATES} templates. Upgrade to add more.`;
}

export function trialTemplateLockedMessage() {
  return "This template is not available during the free trial. Only active templates can be viewed.";
}

export function trialTempTaskLockedMessage() {
  return "Only tasks included in your free-trial templates are available. Other tasks are locked during the trial.";
}

export function isTrialTemplateAccessible(template) {
  return !!(template && template.isActive);
}

export function trialManagerLimitMessage() {
  return `Free trial includes up to ${TRIAL_MAX_MANAGERS} managers. Upgrade to add more.`;
}

export function trialEmployeeUploadLockedMessage() {
  return "Bulk employee upload is not available during the free trial.";
}

export function showTrialRestrictionToast(vm, message) {
  if (!vm || !vm.$swal) return;
  vm.$swal.fire({
    icon: "info",
    title: message,
    confirmButtonColor: "#26527A",
    confirmButtonText: "OK",
  });
}
